import { LoadingOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import React from 'react';
import { BadgeType, Button, Col, Image, Input, PageTitle, PriorityBadge, Row, Table } from 'components';
import { FilterIcon, PlusIcon, VerifiedIcon, ViewIcon } from 'components/common/icons';
import { CountryTable } from 'models/Country';
import { AddClient } from 'pages/admin/clients/addClient';
import { ClientFilterDrawer } from 'pages/admin/clients/filters';
import { useClients } from 'pages/admin/clients/useClients';
import styles from './styles.module.scss';
import { HasAccess } from 'components/common/accessControl';
import { PERMISSIONS } from 'constants/permissions';
import { Tooltip } from 'antd';

const { Search } = Input;

export interface ClientDataType {
  id: string;
  uuid: string | number;
  name: string;
  country: CountryTable;
  dealCount: number;
  amount: number;
  formattedCreatedDate: string;
  formattedLastModifiedDate: string;
  priorityLevel: string;
  priorityRationale: string;
  salesManager: string;
  purchaseManager: string;
  verified: boolean;
}

export const Clients = React.memo(() => {
  const {
    modalState,
    openModal,
    closeModal,
    data,
    isLoading,
    routeChange,
    pagination,
    onSearch,
    closeDrawer,
    openDrawer,
    filterDrawerVisible,
    form,
    handleTableChange,
    applyFilters,
    resetFilters,
    totalElements,
  } = useClients();

  const columns: ColumnsType<ClientDataType> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      render: (_, { uuid }) => {
        return uuid;
      },
    },
    {
      title: 'Client Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      width: 200, // Set max width
      ellipsis: true, // Enable ellipsis
      render: (value, data) => {
        return (
          <Row className={'gap-6'} wrap={false} align={'middle'}>
            {data.verified && <VerifiedIcon color={'#05796B'} size={16} />}
            <Tooltip title={value}>
              <div className={'text-ellipsis'}>{value}</div>
            </Tooltip>
          </Row>
        );
      },
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      render: (country) => {
        return (
          <Row wrap={false} align={'middle'} className={'gap-12'}>
            {country?.fileLocation && (
              <Image
                src={country?.fileLocation}
                width={18}
                height={18}
                preview={false}
                style={{ verticalAlign: 'unset', width: '18px' }}
                className={'radius-circle'}
              />
            )}
            <Col>{country?.name}</Col>
          </Row>
        );
      },
    },
    {
      title: 'Deals Count',
      dataIndex: 'dealCount',
      key: 'dealCount',
      minWidth: 130,
      sorter: true,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      sorter: true,
    },
    {
      title: 'Client type',
      dataIndex: 'clientType',
      key: 'clientType',
      minWidth: 130,
      render: (value) => value?.name,
    },
    {
      title: 'Created Date',
      dataIndex: 'formattedCreatedDate',
      key: 'createDate',
      minWidth: 130,
      sorter: true,
      render: (value) => value?.day,
    },
    {
      title: 'Last Updated',
      dataIndex: 'formattedLastModifiedDate',
      key: 'lastModifiedDate',
      minWidth: 130,
      sorter: true,
      render: (value) => value?.day,
    },
    {
      title: 'Last Activity Date',
      dataIndex: 'formattedLastActivityDate',
      key: 'lastActivityDate',
      minWidth: 160,
      sorter: true,
      render: (value) => value?.day,
    },
    {
      title: 'Priority Level',
      dataIndex: 'priorityLevel',
      key: 'priorityLevel',
      minWidth: 130,
      sorter: true,
      render: (_, data) => {
        return <PriorityBadge tooltip={data.priorityRationale} type={data.priorityLevel as BadgeType} />;
      },
    },
    {
      title: 'Sales Manager',
      dataIndex: 'salesManager',
      key: 'salesManager',
      minWidth: 130,
      render: (value) => `${value?.firstName || ''} ${value?.lastName || ''}`,
    },
    {
      title: 'Purchasing Manager',
      dataIndex: 'purchaseManager',
      key: 'purchaseManager',
      minWidth: 170,
      render: (value) => `${value?.firstName || ''} ${value?.lastName || ''}`,
    },
    {
      fixed: 'right',
      width: '36px',
      key: 'actions',
      render: (value) => (
        <Button onClick={() => routeChange(value.id)} type={'text'} size={'small'} icon={<ViewIcon />} />
      ),
    },
  ];

  return (
    <Col span={24} className={styles.main}>
      <Row gutter={[0, 24]}>
        <Col span={24}>
          <Row align={'middle'} justify={'space-between'}>
            <PageTitle title={'Clients'} />
            <HasAccess role={PERMISSIONS.clients.client.write}>
              <Button type={'primary'} onClick={openModal} icon={<PlusIcon />}>
                Add New
              </Button>
            </HasAccess>
          </Row>
        </Col>
        <Col span={24} className={styles.main_container}>
          <Row>
            <Col span={24}>
              <Row justify={'end'} className={styles.actions_container}>
                <Col style={{ width: '300px' }}>
                  <Search enterButton={false} placeholder={'Search'} onChange={(e) => onSearch(e.target.value)} />
                </Col>
                <Button color="default" variant="filled" onClick={openDrawer} icon={<FilterIcon />} />
              </Row>
            </Col>
            <Col span={24} style={{ paddingTop: '20px' }}>
              <HasAccess role={PERMISSIONS.clients.client.read}>
                <Table
                  tableLayout={'auto'}
                  bordered
                  scroll={{
                    x: true,
                  }}
                  rowKey="id"
                  size={'small'}
                  dataSource={data}
                  columns={columns}
                  loading={{
                    indicator: <LoadingOutlined style={{ fontSize: 48 }} spin />,
                    spinning: isLoading,
                  }}
                  locale={
                    isLoading
                      ? { emptyText: null } // Hide "No Data" text while loading
                      : {} // Use default AntD "No Data" icon when empty
                  }
                  onChange={handleTableChange}
                  pagination={{
                    position: ['bottomCenter'],
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} items`,
                    size: 'default',
                    current: pagination.page,
                    pageSize: pagination.size,
                    total: totalElements,
                  }}
                />
              </HasAccess>
            </Col>
          </Row>
        </Col>
      </Row>
      <ClientFilterDrawer
        form={form}
        closeDrawer={closeDrawer}
        applyFilters={applyFilters}
        resetFilters={resetFilters}
        filterDrawerState={filterDrawerVisible}
      />
      <AddClient modalState={modalState} closeModal={closeModal} />
    </Col>
  );
});
